import React, { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { 
    FaEnvelope, FaPaperPlane, FaUsers, FaFileAlt, FaPlus, FaPlay, FaPause, 
    FaTrash, FaEdit, FaClock, FaCheckCircle, FaTimesCircle, FaSpinner,
    FaCalendarAlt, FaSync, FaEye, FaCopy, FaChartBar
} from 'react-icons/fa';
import { campaignAPI, templateAPI, segmentAPI } from './campaignService';
import TemplateBuilder from './TemplateBuilder';
import SegmentBuilder from './SegmentBuilder';
import CampaignWizard from './CampaignWizard';

// Dark theme styles matching the project
const styles = {
    pageWrapper: {
        display: 'flex',
        justifyContent: 'center',
        width: '100%',
        height: '100%',
        backgroundColor: '#0f1419',
        padding: '30px',
        boxSizing: 'border-box',
        overflowY: 'auto',
        overflowX: 'hidden'
    },
    container: {
        width: '100%',
        maxWidth: '1400px',
        color: '#FFFFFF',
        paddingBottom: '50px'
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '30px',
        paddingBottom: '20px',
        borderBottom: '2px solid rgba(139, 92, 246, 0.3)'
    },
    headerLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: '16px'
    },
    headerIcon: {
        fontSize: '32px',
        color: '#8b5cf6'
    },
    title: {
        fontSize: '28px',
        fontWeight: '700',
        color: '#FFFFFF',
        margin: 0
    },
    subtitle: {
        fontSize: '14px',
        color: 'rgba(255, 255, 255, 0.6)',
        margin: '4px 0 0 0'
    },
    headerActions: {
        display: 'flex',
        gap: '12px'
    },
    primaryBtn: {
        background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
        border: 'none',
        borderRadius: '8px',
        padding: '12px 24px',
        cursor: 'pointer',
        color: '#FFFFFF',
        fontSize: '14px',
        fontWeight: '600',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        transition: 'all 0.2s ease'
    },
    secondaryBtn: {
        background: 'rgba(100, 116, 139, 0.2)',
        border: '1px solid rgba(100, 116, 139, 0.4)',
        borderRadius: '8px',
        padding: '12px 24px',
        cursor: 'pointer',
        color: '#94A3B8',
        fontSize: '14px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        transition: 'all 0.2s ease'
    },
    tabs: {
        display: 'flex',
        gap: '8px',
        marginBottom: '24px',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        paddingBottom: '16px'
    },
    tab: {
        padding: '12px 24px',
        background: 'transparent',
        border: 'none',
        color: '#94A3B8',
        fontSize: '14px',
        fontWeight: '500',
        cursor: 'pointer',
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        transition: 'all 0.2s ease'
    },
    tabActive: {
        background: 'rgba(139, 92, 246, 0.2)',
        color: '#8b5cf6'
    },
    statsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '20px',
        marginBottom: '30px'
    },
    statCard: {
        background: 'rgba(30, 41, 59, 0.8)',
        borderRadius: '12px',
        padding: '24px',
        border: '1px solid rgba(255, 255, 255, 0.05)'
    },
    statIcon: {
        fontSize: '24px',
        marginBottom: '12px'
    },
    statValue: {
        fontSize: '32px',
        fontWeight: '700',
        color: '#FFFFFF',
        marginBottom: '4px'
    },
    statLabel: {
        fontSize: '14px',
        color: 'rgba(255, 255, 255, 0.6)'
    },
    listContainer: {
        background: 'rgba(30, 41, 59, 0.5)',
        borderRadius: '12px',
        border: '1px solid rgba(255, 255, 255, 0.05)',
        overflow: 'hidden'
    },
    listHeader: {
        display: 'grid',
        gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 120px',
        padding: '16px 24px',
        background: 'rgba(0, 0, 0, 0.2)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        fontSize: '12px',
        fontWeight: '600',
        color: 'rgba(255, 255, 255, 0.5)',
        textTransform: 'uppercase',
        letterSpacing: '0.5px'
    },
    listItem: {
        display: 'grid',
        gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 120px',
        padding: '20px 24px',
        alignItems: 'center',
        borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
        transition: 'background 0.2s ease'
    },
    listItemHover: {
        background: 'rgba(139, 92, 246, 0.05)'
    },
    campaignName: {
        fontSize: '15px',
        fontWeight: '500',
        color: '#FFFFFF',
        marginBottom: '4px'
    },
    campaignDesc: {
        fontSize: '13px',
        color: 'rgba(255, 255, 255, 0.5)'
    },
    statusBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '6px 12px',
        borderRadius: '20px',
        fontSize: '12px',
        fontWeight: '500'
    },
    statusDraft: {
        background: 'rgba(100, 116, 139, 0.2)',
        color: '#94A3B8'
    },
    statusScheduled: {
        background: 'rgba(59, 130, 246, 0.2)',
        color: '#3b82f6'
    },
    statusActive: {
        background: 'rgba(16, 185, 129, 0.2)',
        color: '#10b981'
    },
    statusPaused: {
        background: 'rgba(245, 158, 11, 0.2)',
        color: '#f59e0b'
    },
    statusCompleted: {
        background: 'rgba(139, 92, 246, 0.2)',
        color: '#8b5cf6'
    },
    statusFailed: {
        background: 'rgba(239, 68, 68, 0.2)',
        color: '#ef4444'
    },
    actionBtns: {
        display: 'flex',
        gap: '8px'
    },
    iconBtn: {
        background: 'rgba(100, 116, 139, 0.2)',
        border: '1px solid rgba(100, 116, 139, 0.3)',
        borderRadius: '6px',
        padding: '8px',
        cursor: 'pointer',
        color: '#94A3B8',
        fontSize: '14px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.2s ease'
    },
    emptyState: {
        textAlign: 'center',
        padding: '60px 40px',
        color: 'rgba(255, 255, 255, 0.6)'
    },
    emptyIcon: {
        fontSize: '48px',
        color: 'rgba(139, 92, 246, 0.5)',
        marginBottom: '16px'
    },
    emptyTitle: {
        fontSize: '18px',
        fontWeight: '600',
        color: '#FFFFFF',
        marginBottom: '8px'
    },
    emptyDesc: {
        fontSize: '14px',
        marginBottom: '24px'
    },
    modal: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
    },
    modalContent: {
        background: '#1e293b',
        borderRadius: '16px',
        width: '90%',
        maxWidth: '1200px',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 25px 50px rgba(0, 0, 0, 0.5)'
    }
};

const CampaignsPage = () => {
    const { user } = useAuth0();
    const [expertId, setExpertId] = useState(null);
    const [activeTab, setActiveTab] = useState('campaigns');
    const [campaigns, setCampaigns] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [segments, setSegments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(null); // 'template', 'segment', 'campaign', 'wizard'
    const [editingItem, setEditingItem] = useState(null);
    const [stats, setStats] = useState({
        totalCampaigns: 0,
        activeCampaigns: 0,
        totalSent: 0,
        totalRecipients: 0
    });

    // Get expert ID from user
    useEffect(() => {
        if (user?.sub) {
            const id = user.sub.split('|')[1] || user.sub;
            setExpertId(id);
        }
    }, [user]);

    // Fetch data when expert ID is available
    const fetchData = useCallback(async () => {
        if (!expertId) return;
        
        setLoading(true);
        try {
            const [campaignsData, templatesData, segmentsData] = await Promise.all([
                campaignAPI.getCampaigns(expertId),
                templateAPI.getTemplates(expertId),
                segmentAPI.getSegments(expertId)
            ]);

            setCampaigns(campaignsData || []);
            setTemplates(templatesData || []);
            setSegments(segmentsData || []);

            // Calculate stats
            const activeCampaigns = (campaignsData || []).filter(c => 
                ['active', 'scheduled', 'sending'].includes(c.status)
            ).length;
            const totalSent = (campaignsData || []).reduce((sum, c) => sum + (c.stats?.totalSent || 0), 0);
            const totalRecipients = (campaignsData || []).reduce((sum, c) => sum + (c.stats?.totalRecipients || 0), 0);

            setStats({
                totalCampaigns: (campaignsData || []).length,
                activeCampaigns,
                totalSent,
                totalRecipients
            });
        } catch (error) {
            console.error('Error fetching campaign data:', error);
        } finally {
            setLoading(false);
        }
    }, [expertId]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const getStatusStyle = (status) => {
        switch (status) {
            case 'draft': return styles.statusDraft;
            case 'scheduled': return styles.statusScheduled;
            case 'active': return styles.statusActive;
            case 'sending': return styles.statusActive;
            case 'paused': return styles.statusPaused;
            case 'completed': return styles.statusCompleted;
            case 'failed': return styles.statusFailed;
            default: return styles.statusDraft;
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'draft': return <FaFileAlt />;
            case 'scheduled': return <FaClock />;
            case 'active': return <FaPlay />;
            case 'sending': return <FaSpinner className="spin" />;
            case 'paused': return <FaPause />;
            case 'completed': return <FaCheckCircle />;
            case 'failed': return <FaTimesCircle />;
            default: return <FaFileAlt />;
        }
    };

    const handleSendCampaign = async (campaignId) => {
        if (!window.confirm('Are you sure you want to send this campaign now?')) return;
        
        try {
            await campaignAPI.sendCampaign(expertId, campaignId);
            fetchData();
        } catch (error) {
            console.error('Error sending campaign:', error);
            alert('Failed to send campaign: ' + (error.response?.data?.error || error.message));
        }
    };

    const handlePauseCampaign = async (campaignId) => {
        try {
            await campaignAPI.pauseCampaign(expertId, campaignId);
            fetchData();
        } catch (error) {
            console.error('Error pausing campaign:', error);
        }
    };

    const handleResumeCampaign = async (campaignId) => {
        try {
            await campaignAPI.resumeCampaign(expertId, campaignId);
            fetchData();
        } catch (error) {
            console.error('Error resuming campaign:', error);
        }
    };

    const handleDeleteCampaign = async (campaignId) => {
        if (!window.confirm('Are you sure you want to delete this campaign?')) return;
        
        try {
            await campaignAPI.deleteCampaign(expertId, campaignId);
            fetchData();
        } catch (error) {
            console.error('Error deleting campaign:', error);
        }
    };

    const handleDeleteTemplate = async (templateId) => {
        if (!window.confirm('Are you sure you want to delete this template?')) return;
        
        try {
            await templateAPI.deleteTemplate(expertId, templateId);
            fetchData();
        } catch (error) {
            console.error('Error deleting template:', error);
        }
    };

    const handleDeleteSegment = async (segmentId) => {
        if (!window.confirm('Are you sure you want to delete this segment?')) return;
        
        try {
            await segmentAPI.deleteSegment(expertId, segmentId);
            fetchData();
        } catch (error) {
            console.error('Error deleting segment:', error);
        }
    };

    const formatDate = (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const renderCampaignsList = () => (
        <div style={styles.listContainer}>
            <div style={styles.listHeader}>
                <span>Campaign</span>
                <span>Status</span>
                <span>Recipients</span>
                <span>Sent</span>
                <span>Next Run</span>
                <span>Actions</span>
            </div>
            {campaigns.length === 0 ? (
                <div style={styles.emptyState}>
                    <FaPaperPlane style={styles.emptyIcon} />
                    <h3 style={styles.emptyTitle}>No campaigns yet</h3>
                    <p style={styles.emptyDesc}>Create your first email campaign to start reaching your audience</p>
                    <button 
                        style={styles.primaryBtn}
                        onClick={() => setShowModal('wizard')}
                    >
                        <FaPlus /> Create Campaign
                    </button>
                </div>
            ) : (
                campaigns.map(campaign => (
                    <div key={campaign._id} style={styles.listItem}>
                        <div>
                            <div style={styles.campaignName}>{campaign.name}</div>
                            <div style={styles.campaignDesc}>{campaign.description || 'No description'}</div>
                        </div>
                        <div>
                            <span style={{...styles.statusBadge, ...getStatusStyle(campaign.status)}}>
                                {getStatusIcon(campaign.status)}
                                {campaign.status}
                            </span>
                        </div>
                        <div style={{ color: '#FFFFFF' }}>{campaign.stats?.totalRecipients || 0}</div>
                        <div style={{ color: '#10b981' }}>{campaign.stats?.totalSent || 0}</div>
                        <div style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '13px' }}>
                            {formatDate(campaign.nextRunAt)}
                        </div>
                        <div style={styles.actionBtns}>
                            {campaign.status === 'draft' && (
                                <button 
                                    style={{...styles.iconBtn, background: 'rgba(16, 185, 129, 0.2)', color: '#10b981'}}
                                    onClick={() => handleSendCampaign(campaign._id)}
                                    title="Send Now"
                                >
                                    <FaPlay />
                                </button>
                            )}
                            {campaign.status === 'active' && (
                                <button 
                                    style={{...styles.iconBtn, background: 'rgba(245, 158, 11, 0.2)', color: '#f59e0b'}}
                                    onClick={() => handlePauseCampaign(campaign._id)}
                                    title="Pause"
                                >
                                    <FaPause />
                                </button>
                            )}
                            {campaign.status === 'paused' && (
                                <button 
                                    style={{...styles.iconBtn, background: 'rgba(16, 185, 129, 0.2)', color: '#10b981'}}
                                    onClick={() => handleResumeCampaign(campaign._id)}
                                    title="Resume"
                                >
                                    <FaPlay />
                                </button>
                            )}
                            <button 
                                style={styles.iconBtn}
                                onClick={() => {
                                    setEditingItem(campaign);
                                    setShowModal('campaign');
                                }}
                                title="Edit"
                            >
                                <FaEdit />
                            </button>
                            <button 
                                style={{...styles.iconBtn, color: '#ef4444'}}
                                onClick={() => handleDeleteCampaign(campaign._id)}
                                title="Delete"
                            >
                                <FaTrash />
                            </button>
                        </div>
                    </div>
                ))
            )}
        </div>
    );

    const renderTemplatesList = () => (
        <div style={styles.listContainer}>
            <div style={{...styles.listHeader, gridTemplateColumns: '2fr 1fr 1fr 1fr 120px'}}>
                <span>Template</span>
                <span>Category</span>
                <span>Used</span>
                <span>Last Updated</span>
                <span>Actions</span>
            </div>
            {templates.length === 0 ? (
                <div style={styles.emptyState}>
                    <FaFileAlt style={styles.emptyIcon} />
                    <h3 style={styles.emptyTitle}>No templates yet</h3>
                    <p style={styles.emptyDesc}>Create email templates to use in your campaigns</p>
                    <button 
                        style={styles.primaryBtn}
                        onClick={() => setShowModal('template')}
                    >
                        <FaPlus /> Create Template
                    </button>
                </div>
            ) : (
                templates.map(template => (
                    <div key={template._id} style={{...styles.listItem, gridTemplateColumns: '2fr 1fr 1fr 1fr 120px'}}>
                        <div>
                            <div style={styles.campaignName}>{template.name}</div>
                            <div style={styles.campaignDesc}>{template.subject}</div>
                        </div>
                        <div>
                            <span style={{...styles.statusBadge, ...styles.statusActive}}>
                                {template.category}
                            </span>
                        </div>
                        <div style={{ color: '#FFFFFF' }}>{template.usageCount || 0}x</div>
                        <div style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '13px' }}>
                            {formatDate(template.updatedAt)}
                        </div>
                        <div style={styles.actionBtns}>
                            <button 
                                style={styles.iconBtn}
                                onClick={() => {
                                    setEditingItem(template);
                                    setShowModal('template');
                                }}
                                title="Edit"
                            >
                                <FaEdit />
                            </button>
                            <button 
                                style={{...styles.iconBtn, color: '#ef4444'}}
                                onClick={() => handleDeleteTemplate(template._id)}
                                title="Delete"
                            >
                                <FaTrash />
                            </button>
                        </div>
                    </div>
                ))
            )}
        </div>
    );

    const renderSegmentsList = () => (
        <div style={styles.listContainer}>
            <div style={{...styles.listHeader, gridTemplateColumns: '2fr 2fr 1fr 1fr 120px'}}>
                <span>Segment</span>
                <span>Filters</span>
                <span>Est. Recipients</span>
                <span>Last Updated</span>
                <span>Actions</span>
            </div>
            {segments.length === 0 ? (
                <div style={styles.emptyState}>
                    <FaUsers style={styles.emptyIcon} />
                    <h3 style={styles.emptyTitle}>No segments yet</h3>
                    <p style={styles.emptyDesc}>Create audience segments to target specific groups</p>
                    <button 
                        style={styles.primaryBtn}
                        onClick={() => setShowModal('segment')}
                    >
                        <FaPlus /> Create Segment
                    </button>
                </div>
            ) : (
                segments.map(segment => (
                    <div key={segment._id} style={{...styles.listItem, gridTemplateColumns: '2fr 2fr 1fr 1fr 120px'}}>
                        <div>
                            <div style={styles.campaignName}>{segment.name}</div>
                            <div style={styles.campaignDesc}>{segment.description || 'No description'}</div>
                        </div>
                        <div style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '13px' }}>
                            {segment.filters?.funnelStatus?.length > 0 && 
                                `Status: ${segment.filters.funnelStatus.join(', ')}`}
                            {segment.filters?.inactiveDays && 
                                ` | Inactive: ${segment.filters.inactiveDays} days`}
                        </div>
                        <div style={{ color: '#8b5cf6', fontWeight: '600' }}>
                            ~{segment.estimatedCount || '?'}
                        </div>
                        <div style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '13px' }}>
                            {formatDate(segment.updatedAt)}
                        </div>
                        <div style={styles.actionBtns}>
                            <button 
                                style={styles.iconBtn}
                                onClick={() => {
                                    setEditingItem(segment);
                                    setShowModal('segment');
                                }}
                                title="Edit"
                            >
                                <FaEdit />
                            </button>
                            <button 
                                style={{...styles.iconBtn, color: '#ef4444'}}
                                onClick={() => handleDeleteSegment(segment._id)}
                                title="Delete"
                            >
                                <FaTrash />
                            </button>
                        </div>
                    </div>
                ))
            )}
        </div>
    );

    const handleModalClose = () => {
        setShowModal(null);
        setEditingItem(null);
        fetchData();
    };

    if (loading) {
        return (
            <div style={styles.pageWrapper}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', color: '#FFFFFF' }}>
                    <FaSpinner className="spin" style={{ fontSize: '24px' }} />
                    Loading campaigns...
                </div>
            </div>
        );
    }

    return (
        <div style={styles.pageWrapper}>
            <div style={styles.container}>
                {/* Header */}
                <div style={styles.header}>
                    <div style={styles.headerLeft}>
                        <FaEnvelope style={styles.headerIcon} />
                        <div>
                            <h1 style={styles.title}>Campaigns</h1>
                            <p style={styles.subtitle}>Create and manage email campaigns for your audience</p>
                        </div>
                    </div>
                    <div style={styles.headerActions}>
                        <button style={styles.secondaryBtn} onClick={fetchData}>
                            <FaSync /> Refresh
                        </button>
                        <button style={styles.primaryBtn} onClick={() => setShowModal('wizard')}>
                            <FaPlus /> New Campaign
                        </button>
                    </div>
                </div>

                {/* Stats Grid */}
                <div style={styles.statsGrid}>
                    <div style={styles.statCard}>
                        <FaPaperPlane style={{...styles.statIcon, color: '#8b5cf6'}} />
                        <div style={styles.statValue}>{stats.totalCampaigns}</div>
                        <div style={styles.statLabel}>Total Campaigns</div>
                    </div>
                    <div style={styles.statCard}>
                        <FaPlay style={{...styles.statIcon, color: '#10b981'}} />
                        <div style={styles.statValue}>{stats.activeCampaigns}</div>
                        <div style={styles.statLabel}>Active Campaigns</div>
                    </div>
                    <div style={styles.statCard}>
                        <FaEnvelope style={{...styles.statIcon, color: '#3b82f6'}} />
                        <div style={styles.statValue}>{stats.totalSent.toLocaleString()}</div>
                        <div style={styles.statLabel}>Emails Sent</div>
                    </div>
                    <div style={styles.statCard}>
                        <FaUsers style={{...styles.statIcon, color: '#f59e0b'}} />
                        <div style={styles.statValue}>{stats.totalRecipients.toLocaleString()}</div>
                        <div style={styles.statLabel}>Total Recipients</div>
                    </div>
                </div>

                {/* Tabs */}
                <div style={styles.tabs}>
                    <button 
                        style={{...styles.tab, ...(activeTab === 'campaigns' ? styles.tabActive : {})}}
                        onClick={() => setActiveTab('campaigns')}
                    >
                        <FaPaperPlane /> Campaigns ({campaigns.length})
                    </button>
                    <button 
                        style={{...styles.tab, ...(activeTab === 'templates' ? styles.tabActive : {})}}
                        onClick={() => setActiveTab('templates')}
                    >
                        <FaFileAlt /> Templates ({templates.length})
                    </button>
                    <button 
                        style={{...styles.tab, ...(activeTab === 'segments' ? styles.tabActive : {})}}
                        onClick={() => setActiveTab('segments')}
                    >
                        <FaUsers /> Segments ({segments.length})
                    </button>
                </div>

                {/* Content based on active tab */}
                {activeTab === 'campaigns' && renderCampaignsList()}
                {activeTab === 'templates' && renderTemplatesList()}
                {activeTab === 'segments' && renderSegmentsList()}

                {/* Modals */}
                {showModal === 'template' && (
                    <div style={styles.modal} onClick={(e) => e.target === e.currentTarget && handleModalClose()}>
                        <div style={styles.modalContent}>
                            <TemplateBuilder 
                                expertId={expertId}
                                template={editingItem}
                                onClose={handleModalClose}
                                onSave={handleModalClose}
                            />
                        </div>
                    </div>
                )}

                {showModal === 'segment' && (
                    <div style={styles.modal} onClick={(e) => e.target === e.currentTarget && handleModalClose()}>
                        <div style={{...styles.modalContent, maxWidth: '800px'}}>
                            <SegmentBuilder 
                                expertId={expertId}
                                segment={editingItem}
                                onClose={handleModalClose}
                                onSave={handleModalClose}
                            />
                        </div>
                    </div>
                )}

                {showModal === 'wizard' && (
                    <div style={styles.modal} onClick={(e) => e.target === e.currentTarget && handleModalClose()}>
                        <div style={styles.modalContent}>
                            <CampaignWizard 
                                expertId={expertId}
                                templates={templates}
                                segments={segments}
                                onClose={handleModalClose}
                                onComplete={handleModalClose}
                            />
                        </div>
                    </div>
                )}
            </div>

            <style>{`
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
                .spin {
                    animation: spin 1s linear infinite;
                }
            `}</style>
        </div>
    );
};

export default CampaignsPage;
